import { useCallback, useEffect, useState } from 'react';
import {
  PAUSE_POLLING_WHEN_UNFOCUSED,
  SURVEY_SUMMARY_REFRESH_INTERVAL_MS,
} from 'src/constants/polling.constants';
import { useCreateSummaryMutation, useGetSurveySummaryQuery } from 'src/redux/survey/summary.endpoints';
import { SurveyStatus } from 'src/redux/survey/survey.types';

interface UseQuestionSummaryActionsArgs {
  extSurveyId: string;
  isShare?: boolean;
  surveyStatus?: SurveyStatus;
}

export function useQuestionSummaryActions({
  extSurveyId,
  isShare = false,
  surveyStatus,
}: UseQuestionSummaryActionsArgs) {
  const showSummaries = surveyStatus === SurveyStatus.Complete || surveyStatus === SurveyStatus.Live;
  const [regeneratingQuestionIds, setRegeneratingQuestionIds] = useState<string[]>([]);
  const [isGeneratingSurveySummary, setIsGeneratingSurveySummary] = useState(false);
  const [summaryError, setSummaryError] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<number | undefined>(undefined);

  const { data: summary, refetch, error } = useGetSurveySummaryQuery(
    { ext_survey_id: extSurveyId, share: isShare },
    {
      skip: !showSummaries || !extSurveyId,
      pollingInterval,
      ...PAUSE_POLLING_WHEN_UNFOCUSED,
    },
  );

  const [createSummary, { isLoading: isCreatingSummary }] = useCreateSummaryMutation({
    fixedCacheKey: `summary-${extSurveyId}`,
  });

  const isGenerating = isCreatingSummary || isGeneratingSurveySummary || regeneratingQuestionIds.length > 0;

  useEffect(() => {
    if (error) {
      setSummaryError(true);
      setIsGeneratingSurveySummary(false);
      setRegeneratingQuestionIds([]);
      setPollingInterval(undefined);
    }
  }, [error]);

  useEffect(() => {
    if (!summary) {
      return;
    }

    setSummaryError(false);

    if (regeneratingQuestionIds.length > 0) {
      const stillPending = regeneratingQuestionIds.filter((questionId) => {
        const questionSummary = summary.questions.find((entry) => entry.id === questionId);
        if (!questionSummary) {
          return true;
        }
        const hasGeneral = questionSummary.summaries.length > 0;
        const hasSections = Boolean(questionSummary.sections?.some((section) => section.summaries.length > 0));
        return !hasGeneral && !hasSections;
      });

      if (stillPending.length !== regeneratingQuestionIds.length) {
        setRegeneratingQuestionIds(stillPending);
      }

      if (stillPending.length === 0) {
        setPollingInterval(undefined);
      }
      return;
    }

    if (isGeneratingSurveySummary && summary.summaries.length > 0) {
      setIsGeneratingSurveySummary(false);
      setPollingInterval(undefined);
    }
  }, [summary, regeneratingQuestionIds, isGeneratingSurveySummary]);

  const startPolling = useCallback(() => {
    setSummaryError(false);
    setPollingInterval(SURVEY_SUMMARY_REFRESH_INTERVAL_MS);
    void refetch();
  }, [refetch]);

  const regenerateQuestionSummary = useCallback((questionId: string) => {
    if (isShare) {
      return;
    }

    setRegeneratingQuestionIds((current) => (
      current.includes(questionId) ? current : [...current, questionId]
    ));
    startPolling();
    void createSummary({ ext_survey_id: extSurveyId, question_id: questionId });
  }, [createSummary, extSurveyId, isShare, startPolling]);

  const regenerateSurveySummaries = useCallback(() => {
    if (isShare) {
      return;
    }

    setIsGeneratingSurveySummary(true);
    startPolling();
    void createSummary({ ext_survey_id: extSurveyId });
  }, [createSummary, extSurveyId, isShare, startPolling]);

  const isQuestionGenerating = useCallback((questionId: string) => {
    if (regeneratingQuestionIds.includes(questionId)) {
      return true;
    }
    if (isGeneratingSurveySummary) {
      const questionSummary = summary?.questions.find((entry) => entry.id === questionId);
      const hasGeneral = Boolean(questionSummary?.summaries.length);
      const hasSections = Boolean(questionSummary?.sections?.some((section) => section.summaries.length > 0));
      return !hasGeneral && !hasSections;
    }
    return false;
  }, [isGeneratingSurveySummary, regeneratingQuestionIds, summary?.questions]);

  return {
    isGenerating,
    isQuestionGenerating,
    regenerateQuestionSummary,
    regenerateSurveySummaries,
    summaryError,
    canRegenerate: !isShare && !isGenerating,
  };
}
